import React, { useState, useEffect, useRef } from "react";
import { filter, find, get, isEmpty, map, includes } from "lodash";
import TextAreaAutoSize from "react-textarea-autosize";

// PropTypes
import PropTypes from "prop-types";

// Redux
import { connect } from "react-redux";
import genericAction from "@Actions/generic";
// import { setCurrentChat } from "@Actions/chats";

// Components
import { Button, Dropdown, Icon } from "semantic-ui-react";
import IKModal from "@Components/IkModal";
import InputFilter from "@Components/InputFilter";
import ConfirmModal from "@Modals/ConfirmModal";
import Input from "@Components/Input";
import Text from "@Components/Text";
import ModalContactCreate from "./ModalContactCreate";
import ContactInfoModal from "./ContactInfoModal";
import TextAreaInput from "@Components/TextArea";

// Utils
import dataHandler from "@Utils/dataHandler";
import { isWhatsappBlocked } from "../../Utils";

// Static Data
import { contactOption } from "./staticData";

// Styles
import styles from "./styles.module.css";

const ChatModalPlantilla = ({
	onClose,
	onSuccess: onSuccessProp,
	channel,
	contact,
	currentChat,
	channelsData,
	dispatch,
	refresh,
	isReadOnly,
	isHistoric,
	fixedFields,
	userPermissions,
	chats,
}) => {
	const [openConfirm, setOpenConfirm] = useState(false);
	const [openAddContact, setOpenAddContact] = useState(false);
	const [openContactInfo, setOpenContactInfo] = useState(false);
	const [channels, setChannels] = useState([]);
	const [selectedChannel, setSelectedChannel] = useState(channel?.id || null);
	const [contacts, setContacts] = useState(contact?.id ? contact : {});
	const [contactsList, setContactsList] = useState([]);
	const [templates, setTemplates] = useState([]);
	const [selectedTemplate, setSelectedTemplate] = useState(null);
	const [subject, setSubject] = useState("");
	const [body, setBody] = useState("");
	const [message, setMessage] = useState("");
	const [attachments, setAttachments] = useState([]);
	const fileInputRef = useRef();
	const selectedChannelData = channelsData?.find((ch) => {
		return ch.id === selectedChannel;
	});
	const whatsappPlatformNames = ["whatsapp", "whatsapp360", "email"];
	const isWhatsapp = whatsappPlatformNames.includes(selectedChannelData?.platform?.name?.toLowerCase() || "");
	const isEmail = ["email"].includes(selectedChannelData?.platform?.name?.toLowerCase() || "");
	const noSendTemplate = userPermissions?.noSendTemplate;
	const isWhatsappBlock = isHistoric
		? currentChat
			? isWhatsappBlocked(currentChat)
			: false
		: contacts?.requiresTemplate ?? (currentChat ? isWhatsappBlocked(currentChat) : true);
	const activeStartedChat =
		contacts?.id && selectedChannel
			? find(chats, (chat) => {
					return get(chat, "contact.id") === contacts.id && get(chat, "channel.id") === selectedChannel;
			  })
			: null;
	const activeStartedAgent = get(activeStartedChat, "user.fullName") || get(activeStartedChat, "user.nickName");
	const isFormValid = isEmail
		? selectedChannel && contacts.id && !isEmpty(subject) && !isEmpty(body)
		: selectedChannel &&
		  contacts.id &&
		  !activeStartedChat &&
		  (isWhatsapp && isWhatsappBlock ? selectedTemplate && !noSendTemplate : !isEmpty(message));

	const [errors, setErrors] = useState({
		channel: "",
		contact: "",
		template: "",
		subject: "",
		body: "",
		message: "",
	});

	useEffect(() => {
		let isMounted = true;

		const init = async () => {
			setSelectedTemplate(null);

			if (channelsData?.length) {
				const availablePlatforms = ["whatsapp", "whatsapp360", "email"];

				const mappedChannels = map(
					filter(channelsData, (item) => {
						const platformName = item.platform?.name?.toLowerCase() || "";
						return item.isActive && includes(availablePlatforms, platformName);
					}),
					(item) => {
						return {
							key: item.id,
							value: item.id,
							text: `${item.platform?.name} - ${item.label}`,
						};
					}
				);

				if (isMounted) setChannels(mappedChannels);
			}

			const response = await dataHandler({
				dispatch,
				method: "GET",
				url: "/templates",
			});

			if (!isMounted) return;

			const activeTemplates = filter(response.items || [], (tpl) => {
				return tpl.isActive;
			});

			if (selectedChannel) {
				const filteredTemplates = filter(activeTemplates, (tpl) => {
					return tpl.channelId === selectedChannel;
				});

				const mappedTemplates = map(filteredTemplates, (tpl) => {
					return {
						key: tpl.id,
						value: tpl.id,
						text: tpl.title,
					};
				});

				setTemplates(mappedTemplates);
			} else {
				setTemplates([]);
			}
		};

		if (!selectedChannel) {
			setSelectedTemplate(null);
			setTemplates([]);
		}

		init();

		return () => {
			isMounted = false;
		};
	}, [channelsData, dispatch, selectedChannel, channel, contact, currentChat]);

	useEffect(() => {
		if (openAddContact) {
			const fetchContacts = async () => {
				const response = await dataHandler({
					dispatch,
					method: "GET",
					url: "contacts/noChat",
					params: selectedChannel ? { "contact.channel": selectedChannelData?.platform?.name } : {},
				});
				setContactsList(response.items || []);
			};
			fetchContacts();
		}
	}, [openAddContact, dispatch, selectedChannel]);

	const handleCancel = () => {
		const hasChanges = selectedChannel || contacts?.id || selectedTemplate || !isEmpty(message);
		if (!hasChanges || isReadOnly || isHistoric) {
			onClose();
		} else {
			setOpenConfirm(true);
		}
	};

	const handleConfirmClose = () => {
		setOpenConfirm(false);
		onClose();
	};

	const onSuccess = () => {
		setSelectedChannel(null);
		setContacts({});
		setSelectedTemplate(null);
		setSubject("");
		setBody("");
		setAttachments([]);
		setErrors({ channel: "", contact: "", template: "", subject: "", body: "" });
		refresh?.();
		onSuccessProp?.();
		onClose();
	};

	const handlerSaved = async () => {
		if (isEmail) {
			if (!selectedChannel || !contacts?.id || isEmpty(subject) || isEmpty(body)) {
				setErrors({
					channel: !selectedChannel ? "El canal es requerido" : "",
					contact: !contacts?.id ? "El contacto es requerido" : "",
					subject: isEmpty(subject) ? "El asunto es requerido" : "",
					body: isEmpty(body) ? "El cuerpo es requerido" : "",
					message: isEmpty(message) ? "El mensaje es requerido" : "",
				});
				return;
			}
			const payload = {
				channelId: selectedChannel,
				contactId: contacts.id,
				to: contacts.email,
				subject,
				body,
				message,
				attachments,
			};
			await dispatch(
				genericAction({
					method: "post",
					route: "emails/sendNewEmail",
					body: payload,
					onSuccess: () => {
						onSuccess();
						onClose();
					},
				})
			);
		} else {
			const needsTemplate = isWhatsapp && isWhatsappBlock;
			const hasTemplate = selectedTemplate;
			const hasMessage = !isEmpty(message);

			if (!selectedChannel || !contacts?.id || (needsTemplate && !hasTemplate) || (!needsTemplate && !hasMessage)) {
				setErrors({
					channel: !selectedChannel ? "El canal es requerido" : "",
					contact: !contacts?.id ? "El contacto es requerido" : "",
					template: needsTemplate && !hasTemplate ? "La plantilla es requerida" : "",
					message: !needsTemplate && !hasMessage ? "El mensaje es requerido" : "",
				});
				return;
			}

			if (needsTemplate) {
				const payload = {
					channelId: selectedChannel,
					contactIds: [contacts.id],
					templateId: selectedTemplate,
					message,
				};

				await dispatch(
					genericAction({
						method: "post",
						route: "templates/send",
						body: payload,
						onSuccess: () => {
							onSuccess();
							onClose();
						},
					})
				);
			} else {
				const channelCid = currentChat?.channelCid || contacts?.contact?.channelAccountId || contacts?.phone || "";
				const payload = {
					channel: selectedChannelData,
					channelCid: String(channelCid),
					text: message,
					contactId: contacts.id,
				};

				await dispatch(
					genericAction({
						method: "post",
						route: "chats/newChat",
						body: payload,
						onSuccess: () => {
							onSuccess();
							onClose();
						},
					})
				);
			}
		}
	};

	const handleInputChange = (e) => {
		const { name, value } = e.target;
		switch (name) {
			case "subject": {
				setSubject(value);
				break;
			}
			case "body": {
				setBody(value);
				break;
			}
			case "message": {
				setMessage(value);
				break;
			}
			default:
				break;
		}
	};

	const handleFileClick = () => {
		fileInputRef?.current?.click();
	};

	const handleFileInput = async (e) => {
		const filesFromInput = [];
		for (const file of e.target.files) {
			const base64File = await new Promise((resolve) => {
				const reader = new FileReader();
				reader.onloadend = () => {
					resolve({
						name: file.name,
						type: file.type,
						base64: reader.result,
					});
				};
				reader.readAsDataURL(file);
			});
			filesFromInput.push(base64File);
		}

		setAttachments((prev) => {
			return [...prev, ...filesFromInput];
		});
		fileInputRef.current.value = null;
	};

	const removeFile = (index) => {
		setAttachments((prev) => {
			return prev.filter((_, i) => {
				return i !== index;
			});
		});
	};

	const renderFiles = () => {
		if (isEmpty(attachments)) return null;
		const rows = map(attachments, (item, index) => {
			return (
				<div className={styles.fileContainer} key={index}>
					<div className={styles.messageContainer}>
						<Text>{item.name}</Text>
					</div>
					<div
						className={styles.fileIconContainer}
						role="button"
						tabIndex="-1"
						onClick={() => {
							return removeFile(index);
						}}>
						<Icon name="times circle outline" size="large" />
					</div>
				</div>
			);
		});
		return rows;
	};

	const handleChannelChange = (value) => {
		setSelectedChannel(value);

		if (!value) {
			setContacts({});
			setTemplates([]);
			setSelectedTemplate(null);
			setErrors((prev) => {
				return {
					...prev,
					contact: "",
					template: "",
				};
			});
		}
	};

	return (
		<>
			<IKModal
				title="Iniciar Chats"
				onClose={handleCancel}
				onSuccess={handlerSaved}
				saveLabel="Iniciar"
				saveDisabled={!isFormValid}
				cancelLabel="Salir"
				onCancel={handleCancel}
				size={isEmail ? "large" : "tiny"}
				fullHeight>
				<div className={styles.grid}>
					<fieldset className={styles.fieldset}>
						<legend className={styles.legend}>Canal</legend>
						<Dropdown
							placeholder="Selecciona un canal"
							fluid
							selection
							options={channels}
							value={selectedChannel}
							onChange={(e, { value }) => {
								return handleChannelChange(value);
							}}
							disabled={isReadOnly}
							className={isEmail ? styles.widthCanal : ""}
							clearable
						/>
						{errors.channel && <span className={styles.error}>{errors.channel}</span>}
					</fieldset>

					<fieldset className={styles.fieldset}>
						<legend className={styles.legend}>
							Contacto
							{contacts?.id && (
								<Icon
									name="eye"
									link
									onClick={() => {
										return setOpenContactInfo(true);
									}}
									title="Ver información del contacto"
									style={{ marginLeft: "8px", cursor: "pointer" }}
								/>
							)}
						</legend>
						<div className={styles.contactFieldContainer}>
							<InputFilter
								contactData={contacts}
								selectedContact={setContacts}
								onEmpty={() => {
									return setContacts({});
								}}
								includeEmail={isEmail}
								includePhone={isWhatsapp}
								url="contacts/noChat"
								defaultField="all"
								isWhatsapp={isWhatsapp}
								forceInputValue="name"
								selectTitle="Buscar Contacto"
								tableColumns={contactOption}
								mobileCardView
								cardLine1={[
									{ label: "ID", key: "id" },
									{ label: "", key: "name" },
									{ label: "Plat", key: "contact.channel" },
								]}
								cardLine2={[{ label: "Tel", key: "phone" }]}
								disabled={isReadOnly || !selectedChannel}
								urlParams={selectedChannelData ? { "contact.channel": selectedChannelData.platform.name } : {}}
								onAddButtonClick={() => {
									return setOpenAddContact(true);
								}}
								inputClassName={isEmail ? styles.widthCanal : ""}
							/>

							{isEmpty(contacts) && selectedChannel && (
								<Button
									className={styles.buttonClass}
									onClick={() => {
										return setOpenAddContact(true);
									}}>
									<Icon name="plus" className={styles.icon} />
									Nuevo Contacto
								</Button>
							)}
						</div>
						{errors.contact && <span className={styles.error}>{errors.contact}</span>}
					</fieldset>

					{activeStartedChat && (
						<fieldset className={styles.fieldset}>
							<legend className={styles.legend}>Atención</legend>
							<span className={styles.errorText}>
								Ya existe un chat abierto con este contacto. Está siendo atendido por {activeStartedAgent || "otro agente"}.
							</span>
						</fieldset>
					)}

					{!isEmail && isWhatsapp && contacts?.id && !activeStartedChat && isWhatsappBlock && (
						<fieldset className={styles.fieldset}>
							<legend className={styles.legend}>Plantilla</legend>
							{noSendTemplate ? (
								<span className={styles.errorText}>No tiene permisos para enviar plantillas.</span>
							) : (
								<>
									<Dropdown
										placeholder="Selecciona una plantilla"
										fluid
										selection
										clearable
										options={templates}
										value={selectedTemplate}
										onChange={(e, { value }) => {
											return setSelectedTemplate(value);
										}}
										disabled={!selectedChannel}
										className={isEmail ? styles.widthCanal : ""}
									/>
									{errors.template && <span className={styles.error}>{errors.template}</span>}
								</>
							)}
						</fieldset>
					)}

					{!isEmail && contacts?.id && isWhatsapp && !activeStartedChat && !isWhatsappBlock && (
						<fieldset className={styles.fieldset}>
							<legend className={styles.legend}>Mensaje</legend>
							<TextAreaInput
								id="1"
								name="message"
								className={styles.textarea}
								placeholder="Escriba un mensaje"
								value={message}
								onChange={handleInputChange}
								minRows={4}
							/>
							{errors.message && <span className={styles.error}>{errors.message}</span>}
						</fieldset>
					)}

					{isEmail && !activeStartedChat && (
						<fieldset className={styles.fieldset}>
							<legend className={styles.legend}>Asunto</legend>
							<Input placeholder="Escriba el asunto" fluid name="subject" value={subject} onChange={handleInputChange} />
							{errors.subject && <span className={styles.error}>{errors.subject}</span>}
						</fieldset>
					)}

					{isEmail && !activeStartedChat && (
						<fieldset className={styles.fieldset}>
							<legend className={styles.legend}>Cuerpo</legend>
							<TextAreaAutoSize
								id="1"
								name="body"
								className={styles.textarea}
								placeholder="Escriba un mensaje"
								value={body}
								onChange={handleInputChange}
								minRows={10}
								maxRows={10}
							/>
							{errors.body && <span className={styles.error}>{errors.body}</span>}
						</fieldset>
					)}

					{isEmail && !activeStartedChat && (
						<fieldset className={styles.fieldset}>
							<legend className={styles.legend}>Adjuntos</legend>
							<input ref={fileInputRef} type="file" hidden multiple accept="*/*" onChange={handleFileInput} />
							<Button className={styles.buttonClass} onClick={handleFileClick}>
								<Icon name="plus" className={styles.icon} />
								Nuevo Adjunto
							</Button>
							{renderFiles()}
						</fieldset>
					)}
				</div>
			</IKModal>

			{openAddContact && (
				<ModalContactCreate
					open={openAddContact}
					onClose={() => {
						return setOpenAddContact(false);
					}}
					onSave={(newContact) => {
						return setContacts(newContact);
					}}
					dispatch={dispatch}
					contacts={contactsList}
					isEmail={isEmail}
					fixedFields={fixedFields}
				/>
			)}

			{openContactInfo && (
				<ContactInfoModal
					open={openContactInfo}
					onClose={() => {
						return setOpenContactInfo(false);
					}}
					contact={contacts}
				/>
			)}

			{openConfirm && (
				<ConfirmModal
					buttonLabel="Salir"
					visible
					header="Confirmar Cerrar"
					fullContent="¿Deseas salir sin guardar los cambios?"
					onConfirm={handleConfirmClose}
					onDismiss={() => {
						return setOpenConfirm(false);
					}}
				/>
			)}
		</>
	);
};

ChatModalPlantilla.propTypes = {
	onClose: PropTypes.func,
	onSuccess: PropTypes.func,
	channel: PropTypes.object,
	contact: PropTypes.object,
	currentChat: PropTypes.object,
	dispatch: PropTypes.func,
	channelsData: PropTypes.array,
	chats: PropTypes.array,
	refresh: PropTypes.func,
	isReadOnly: PropTypes.bool,
	isHistoric: PropTypes.bool,
	fixedFields: PropTypes.array,
	userPermissions: PropTypes.object,
};

function mapStateToProps({ company, user, chats }) {
	return {
		channelsData: company.channels,
		fixedFields: company.settings?.fixedFields || [],
		userPermissions: user.permissions,
		chats: chats.chats,
	};
}

export default connect(mapStateToProps)(ChatModalPlantilla);
